/**
 * Utility functions for handling phone numbers and UUIDs
 */

/**
 * Parses a phone number that may contain a UUID separated by '::'
 * @param {string} phoneNumber - The phone number string (e.g., "+19547953645::5b6fc7c3-96f4-4eea-b8d6-cf5df59a454f")
 * @returns {object} - Object with 'phone' and 'uuid' properties
 */
export function parsePhoneNumber(phoneNumber) {
  if (!phoneNumber)
    return { phone: '', uuid: null }

  const parts = phoneNumber.split('::')
  if (parts.length === 2) {
    return { phone: parts[0], uuid: parts[1] }
  }
  return { phone: phoneNumber, uuid: null }
}

/**
 * Truncates a UUID for display purposes
 * @param {string} uuid - The UUID string to truncate
 * @param {number} maxLength - Maximum length before truncation (default: 8)
 * @returns {string} - Truncated UUID with ellipsis if needed
 */
export function getTruncatedUuid(uuid, maxLength = 8) {
  if (!uuid)
    return ''
  if (uuid.length <= maxLength)
    return uuid
  return `${uuid.substring(0, maxLength)}...`
}

/**
 * Formats a phone number for display with optional UUID
 * @param {string} phoneNumber - The full phone number string
 * @param {object} options - Formatting options
 * @param {boolean} options.truncateUuid - Whether to truncate the UUID (default: true)
 * @param {number} options.uuidMaxLength - Maximum UUID length before truncation (default: 8)
 * @returns {object} - Object with formatted phone and uuid
 */
export function formatPhoneForDisplay(phoneNumber, options = {}) {
  const { truncateUuid = true, uuidMaxLength = 8 } = options
  const { phone, uuid } = parsePhoneNumber(phoneNumber)

  return {
    phone,
    uuid: uuid && truncateUuid ? getTruncatedUuid(uuid, uuidMaxLength) : uuid,
    fullUuid: uuid, // Always keep the full UUID available
  }
}
